import { useState, useMemo } from 'react';
import { useShelterStore } from '../../../store/shelterStore';
import { notifications } from '../../../lib/notifications';
import type { Shelter } from '../../../types/shelter';
import { IconSearch, IconCheck, IconArrowRight, IconX } from '@tabler/icons-react';

interface Props {
  shelter: Shelter;
  allShelters: Shelter[];
}

function age(dob: string): string {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  return `${Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000))}y`;
}

export default function ShelterPortalTransfer({ shelter, allShelters }: Props) {
  const { survivors, updateSurvivor, loadShelters, loadSurvivors } = useShelterStore();

  const [personSearch, setPersonSearch] = useState('');
  const [selectedId, setSelectedId]     = useState<number | null>(null);
  const [destId, setDestId]             = useState<string>('');
  const [otherDest, setOtherDest]       = useState('');
  const [notes, setNotes]               = useState('');
  const [saving, setSaving]             = useState(false);
  const [done, setDone]                 = useState(false);

  const active = useMemo(() =>
    survivors.filter(s => s.shelterId === String(shelter.id) && s.status === 'checked_in'),
    [survivors, shelter.id],
  );

  const filtered = useMemo(() => {
    if (!personSearch) return active;
    const q = personSearch.toLowerCase();
    return active.filter(s => `${s.firstName} ${s.lastName}`.toLowerCase().includes(q));
  }, [active, personSearch]);

  const selected = useMemo(() => active.find(s => s.id === selectedId) ?? null, [active, selectedId]);

  const isOther     = destId === 'other';
  const destShelters = allShelters.filter(s => s.id !== shelter.id && s.status !== 'closed');
  const destShelter  = destShelters.find(s => String(s.id) === destId) ?? null;
  const destLabel    = isOther ? (otherDest.trim() || 'Other') : destShelter?.name ?? '';

  const canTransfer = selected && destId && (isOther ? otherDest.trim().length > 0 : !!destShelter);

  const handleTransfer = async () => {
    if (!selected || !destId) return;
    setSaving(true);
    try {
      if (isOther) {
        // External destination (hospital, etc.) — check out of shelter system
        await updateSurvivor(selected.id, { status: 'checked_out' as any });
      } else {
        await updateSurvivor(selected.id, { shelterId: destId });
      }
      await Promise.all([loadShelters(), loadSurvivors()]);
      setDone(true);
      setTimeout(() => {
        setDone(false);
        setSelectedId(null);
        setDestId('');
        setOtherDest('');
        setNotes('');
        setPersonSearch('');
      }, 2200);
    } catch {
      notifications.show({ title: 'Error', message: 'Transfer failed. Please try again.', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 16px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: '#dcfce7',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <IconCheck size={32} color="#16a34a" />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d', marginBottom: 8 }}>Transferred!</div>
        <div style={{ fontSize: 14, color: '#7B7D7F' }}>
          {selected?.firstName} {selected?.lastName} moved to {destLabel || 'new destination'}.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Step 1 — pick person */}
      <div className="sp-card">
        <div className="sp-card-title">Step 1 — Select Resident</div>

        <div style={{ position: 'relative', marginBottom: 8 }}>
          <IconSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
          <input
            className="sp-input"
            style={{ paddingLeft: 34 }}
            placeholder="Search by name…"
            value={personSearch}
            onChange={e => { setPersonSearch(e.target.value); setSelectedId(null); }}
          />
        </div>

        {selected ? (
          /* Selected person chip */
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#eeeeff', border: '1.5px solid #4B3FFF', borderRadius: 4, padding: '8px 16px',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1B1C1C' }}>{selected.firstName} {selected.lastName}</div>
              <div style={{ fontSize: 12, color: '#7B7D7F' }}>
                {age(selected.dateOfBirth)}{age(selected.dateOfBirth) && selected.gender ? ' · ' : ''}{selected.gender}
                {selected.cotAssignment ? ` · Cot ${selected.cotAssignment}` : ''}
              </div>
            </div>
            <button onClick={() => setSelectedId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7B7D7F' }}>
              <IconX size={16} />
            </button>
          </div>
        ) : (
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#939598', fontSize: 14 }}>
                {active.length === 0 ? 'No residents currently checked in.' : 'No results.'}
              </div>
            ) : filtered.slice(0, 40).map(s => (
              <div
                key={s.id}
                onClick={() => { setSelectedId(s.id); setPersonSearch(''); }}
                style={{
                  padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{s.firstName} {s.lastName}</div>
                  <div style={{ fontSize: 12, color: '#7B7D7F' }}>{age(s.dateOfBirth)}{s.cotAssignment ? ` · Cot ${s.cotAssignment}` : ''}</div>
                </div>
                <IconArrowRight size={14} color="#939598" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Step 2 — destination */}
      <div className="sp-card">
        <div className="sp-card-title">Step 2 — Destination Shelter</div>
        <select
          className="sp-input sp-select"
          value={destId}
          onChange={e => { setDestId(e.target.value); setOtherDest(''); }}
          disabled={!selected}
          style={{ opacity: selected ? 1 : 0.5 }}
        >
          <option value="">— Select destination —</option>
          {destShelters.map(s => {
            const avail = s.capacity ? Math.max(0, s.capacity - s.currentPopulation) : '?';
            return (
              <option key={s.id} value={String(s.id)}>
                {s.name} ({avail} available)
              </option>
            );
          })}
          <option value="other">Other (Hospital, External, etc.)</option>
        </select>

        {isOther && (
          <div className="sp-field" style={{ marginTop: 8, marginBottom: 0 }}>
            <label className="sp-label">Destination name <span className="sp-req">*</span></label>
            <input
              className="sp-input"
              value={otherDest}
              onChange={e => setOtherDest(e.target.value)}
              placeholder="e.g. Memorial Hospital, FEMA Hotel Program…"
            />
          </div>
        )}

        {!isOther && destShelter && (
          <div style={{ marginTop: 8, padding: '8px 16px', background: '#F8F8F8', borderRadius: 4, fontSize: 14, color: '#475569' }}>
            📍 {destShelter.address}{destShelter.city ? `, ${destShelter.city}` : ''}
            {destShelter.capacity && (
              <span style={{ marginLeft: 8, color: destShelter.currentPopulation >= destShelter.capacity ? '#B12525' : '#16a34a', fontWeight: 600 }}>
                · {Math.max(0, destShelter.capacity - destShelter.currentPopulation)} beds open
              </span>
            )}
          </div>
        )}
      </div>

      {/* Step 3 — notes */}
      <div className="sp-card">
        <div className="sp-card-title">Notes (optional)</div>
        <textarea
          className="sp-textarea"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Reason for transfer, special instructions…"
          style={{ minHeight: 64 }}
        />
      </div>

      {/* Confirm transfer summary */}
      {canTransfer && (
        <div style={{
          background: '#eeeeff', border: '1.5px solid #4B3FFF',
          borderRadius: 4, padding: '16px 16px', marginBottom: 16, fontSize: 14,
        }}>
          <div style={{ fontWeight: 700, color: '#2C01D6', marginBottom: 4 }}>Transfer Summary</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1B1C1C' }}>
            <span style={{ fontWeight: 600 }}>{selected.firstName} {selected.lastName}</span>
            <IconArrowRight size={14} />
            <span style={{ fontWeight: 600 }}>{destLabel}</span>
          </div>
          {isOther && (
            <div style={{ fontSize: 12, color: '#92400e', marginTop: 4 }}>
              ⚠ Resident will be checked out of the shelter system.
            </div>
          )}
        </div>
      )}

      <button
        className="sp-btn sp-btn-primary"
        onClick={handleTransfer}
        disabled={!canTransfer || saving}
        style={{ marginTop: 4 }}
      >
        {saving ? 'Transferring…' : 'Confirm Transfer'}
      </button>
    </div>
  );
}
